/**
 * liveInventoryCategories
 * -----------------------
 * The uploaded supplier sheets carry no category column, so we derive one
 * from the description + manufacturer text. Used by the public live
 * backorder list to render one-tap filter chips.
 */
export const LIVE_INVENTORY_CATEGORIES = [
  {
    id: "skin-prep",
    label: "Skin Prep & Antiseptics",
    keywords: ["chloraprep", "chlorhexidine", "chg", "betadine", "povidone", "alcohol prep", "prep pad", "avagard", "antiseptic", "hibiclens", "duraprep", "skin prep", "iodine"],
  },
  {
    id: "surgical",
    label: "Surgical & OR",
    keywords: ["surgical", "sponge", "lap pad", "laparotomy", "drape", "suture", "scalpel", "blade", "gauze", "trocar", "stapler", "cautery", "bovie", "or towel", "surgical gown", "electrode pad", "retractor", "kittner", "peanut", "pds ii", "vicryl", "prolene", "monocryl", "biopsy", "chest drain", "thora", "clip applier", "specimen bag"],
  },
  {
    id: "electrosurgical",
    label: "Electrosurgical & Energy",
    keywords: ["ligasure", "sealer / divider", "sealer/divider", "vessel seal", "harmonic", "dissector", "generator", "enseal", "sonicision"],
  },
  {
    id: "patient-warming",
    label: "Patient Warming",
    keywords: ["bair hugger", "warming blanket", "warming gown", "fluid warming", "blood/fluid warm", "ranger", "warming unit", "hypothermia"],
  },
  {
    id: "enteral",
    label: "Enteral & Fluid Transfer",
    keywords: ["enteral", "kangaroo", "enfit", "feeding", "gravity bag", "gravity fill", "eva bag", "eva gravity", "eva legless", "pump bag set", "fluid transfer", "intravia", "evacuated", "transfer set"],
  },
  {
    id: "monitoring",
    label: "Monitoring & Emergency",
    keywords: ["cuff bp", "bp cuff", "cuff reus", "blood pressure", "aed", "defibrillator", "pulse ox", "spo2", "telemetry", "cpr", "zoll"],
  },
  {
    id: "wound-care",
    label: "Wound Care & Dressings",
    keywords: ["dressing", "bandage", "tegaderm", "hydrocolloid", "foam dressing", "alginate", "wound", "gauze pad", "tape", "cohesive", "coban", "island dressing", "silicone"],
  },
  {
    id: "iv-infusion",
    label: "IV & Infusion",
    keywords: ["iv ", "i.v.", "catheter", "needle", "syringe", "infusion", "tubing", "extension set", "cannula", "angiocath", "port", "heparin", "saline flush", "administration set", "luer", "stopcock", "pump set", "injection site", "pressure infusor", "vented spike", "male ll", "ultrasite", "connector spiros", "interlink"],
  },
  {
    id: "respiratory",
    label: "Anesthesia & Respiratory",
    keywords: ["anesthesia", "respiratory", "oxygen", "nasal cannula", "endotracheal", "ett", "laryngeal", "circuit", "nebulizer", "suction", "tracheostomy", "trach", "ventilator", "spirometer", "humidifier", "co2", "capnography", "bag valve", "ambu"],
  },
  {
    id: "gloves-ppe",
    label: "Gloves & PPE",
    keywords: ["glove", "glve", "n95", "respirator", "face shield", "isolation gown", "shoe cover", "bouffant", "coverall", "goggle", "face mask", "procedure mask", "ppe", "apron", "face cover", "ear loop", "chemotherapy gown", "mask,"],
  },
  {
    id: "lab-diagnostics",
    label: "Lab & Diagnostics",
    keywords: ["swab", "culture", "specimen", "slide", "lancet", "blood collection", "vacutainer", "reagent", "test kit", "assay", "petri", "ecg", "ekg", "electrode", "thermometer probe", "urinalysis", "pipette", "centrifuge"],
  },
  {
    id: "sterilization",
    label: "Sterilization & CS",
    keywords: ["sterilization", "sterilizer", "autoclave", "indicator", "biological indicator", "sterilization pouch", "sterilization wrap", "csr wrap", "instrument tray", "enzymatic", "instrument cleaner", "chemical indicator", "peel pouch", "cassette", "attest", "chemical integrator"],
  },
  {
    id: "patient-care",
    label: "Patient Care & EVS",
    keywords: ["wipe", "disinfectant", "underpad", "brief", "incontinence", "bedpan", "linen", "washcloth", "basin", "mop", "bath", "patient gown", "sanitizer", "cleaner", "urinal", "emesis", "sharps container", "trash liner", "ostomy", "foley", "crutches", "walker", "wheelchair", "cane"],
  },
];

const OTHER = { id: "other", label: "Other" };

const haystack = (row) =>
  `${row["Description"] || ""} ${row["Manufacturer"] || ""} ${row["MFR #"] || ""}`.toLowerCase();

export function classifyRow(row) {
  const text = haystack(row);
  for (const cat of LIVE_INVENTORY_CATEGORIES) {
    if (cat.keywords.some((k) => text.includes(k))) return cat.id;
  }
  return OTHER.id;
}

/** Build the chip list (with live counts) for a set of rows. */
export function buildCategoryChips(rows) {
  const counts = new Map();
  for (const r of rows || []) {
    const id = classifyRow(r);
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  const chips = LIVE_INVENTORY_CATEGORIES
    .filter((c) => counts.get(c.id))
    .map((c) => ({ id: c.id, label: c.label, count: counts.get(c.id) }));
  if (counts.get(OTHER.id)) {
    chips.push({ id: OTHER.id, label: OTHER.label, count: counts.get(OTHER.id) });
  }
  return chips;
}
